import { Fragment, useMemo, useState } from "react";

import dayjs from "dayjs";
import {
  ArrowLeftRight,
  ArrowRight,
  ChevronRight,
  Download,
  Loader2,
  Plus,
  Printer,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { PageHeaderSkeleton, TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { getProductionUnitLabel } from "~/constants/units";
import {
  useFgPlantTransfers,
  useIngredientTransfers,
  usePendingReceipts,
  useProductTransfers,
} from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import { usePagination } from "~/hooks/usePagination";
import { cn } from "~/lib/utils";
import { productTransferService } from "~/services/purchasing";
import { PLANT_LABELS, warehouseToPlantLabel } from "~/types/purchasing";
import { exportCsv } from "~/utils/csv";
import { generateInternalTransferPdf } from "~/utils/internalTransferPdf";

import { NewTransferDialog } from "./components/NewTransferDialog";
import { TransferReceptionTab } from "./components/TransferReceptionTab";

import type {
  IngredientTransferItemWithNames,
  IngredientTransferWithItems,
} from "~/types/ingredientTransfer";
import type {
  FgPlantTransferWithNames,
  InternalTransferGuideData,
  ProductTransferWithNames,
} from "~/types/productTransfer";
import type { Plant } from "~/types/purchasing";


const fmtQty = (n: number) =>
  n.toLocaleString("es-CL", { maximumFractionDigits: 2 });

const NEUTRAL_CHIP = "border-gray-300 bg-gray-100 text-gray-600";

// Semantic status chips for producto-terminado transfers (transitorio flow).
const PRODUCT_STATUS_META: Record<string, { label: string; className: string }> =
  {
    in_transit: {
      label: "En tránsito",
      className: "border-amber-300 bg-amber-50 text-amber-700",
    },
    completed: {
      label: "Recibido",
      className: "border-green-300 bg-green-50 text-green-700",
    },
    cancelled: { label: "Anulado", className: NEUTRAL_CHIP },
  };

type TransferKind = "ingredient" | "product" | "fg_plant";

// A single chronological row across every transfer kind.
interface UnifiedTransfer {
  kind: TransferKind;
  id: string;
  transferNumber: string;
  createdAt: string;
  status: string;
  fromLabel: string;
  toLabel: string;
  summary: string;
  performer: string;
  notes: string | null;
  ingredientItems?: IngredientTransferItemWithNames[];
  product?: {
    name: string;
    sku: string | null;
    lote: string;
    boxes: number;
    unit: string | null;
  };
}

const performerName = (p?: {
  name: string | null;
  lastname: string | null;
} | null) => {
  if (!p) return "—";
  const full = `${p.name ?? ""} ${p.lastname ?? ""}`.trim();
  return full || "—";
};

const ingredientItemName = (it: IngredientTransferItemWithNames) =>
  it.oil_type?.name ?? it.raw_material?.name ?? "—";

const normalizeIngredient = (
  t: IngredientTransferWithItems
): UnifiedTransfer => ({
  kind: "ingredient",
  id: t.id,
  transferNumber: t.transfer_number,
  createdAt: t.created_at,
  status: "completed",
  fromLabel: PLANT_LABELS[t.from_plant as Plant] ?? t.from_plant,
  toLabel: PLANT_LABELS[t.to_plant as Plant] ?? t.to_plant,
  summary: `${t.items.length} ${t.items.length === 1 ? "ingrediente" : "ingredientes"}`,
  performer: performerName(t.performed_by_profile),
  notes: t.notes,
  ingredientItems: t.items,
});

const normalizeProduct = (t: ProductTransferWithNames): UnifiedTransfer => {
  const lote =
    t.lot_number ??
    t.production_batch?.lot_number ??
    t.production_batch?.batch_number ??
    "Sin lote";
  const name = t.product?.short_name ?? "Producto";
  const sku = t.product?.sku ?? null;
  const unit = t.product?.base_measurement_unit ?? t.product?.unit_code ?? null;
  return {
    kind: "product",
    id: t.id,
    transferNumber: t.transfer_number,
    createdAt: t.created_at,
    status: t.status,
    fromLabel: warehouseToPlantLabel(t.from_warehouse?.code),
    toLabel: warehouseToPlantLabel(t.to_warehouse?.code),
    summary: `${sku ? `${sku} · ` : ""}${name} · ${fmtQty(t.boxes)} ${getProductionUnitLabel(unit, t.boxes)}`,
    performer: performerName(t.performed_by_profile),
    notes: t.notes,
    product: { name, sku, lote, boxes: t.boxes, unit },
  };
};

// FG-between-plants transfers (P1↔P2↔NC) move produced-but-not-yet-ingested stock.
// They complete instantly (no reception flow), so they read as "Completado".
const normalizeFgPlant = (t: FgPlantTransferWithNames): UnifiedTransfer => {
  const name = t.product?.short_name ?? "Producto";
  const sku = t.product?.sku ?? null;
  const unit = t.product?.base_measurement_unit ?? t.product?.unit_code ?? null;
  return {
    kind: "fg_plant",
    id: t.id,
    transferNumber: t.transfer_number,
    createdAt: t.created_at,
    status: "completed",
    fromLabel: PLANT_LABELS[t.from_plant as Plant] ?? t.from_plant,
    toLabel: PLANT_LABELS[t.to_plant as Plant] ?? t.to_plant,
    summary: `${sku ? `${sku} · ` : ""}${name} · ${fmtQty(t.boxes)} ${getProductionUnitLabel(unit, t.boxes)}`,
    performer: performerName(t.performed_by_profile),
    notes: t.notes,
    product: {
      name,
      sku,
      lote: t.lot_number ?? "Sin lote",
      boxes: t.boxes,
      unit,
    },
  };
};

// Build the printable "Guía de Traslado Interno" straight from an already-loaded row
// (no extra fetch). Producto terminado (product/fg_plant) -> one line; ingredientes ->
// one line per item (oils shown in kg, mirroring the expanded-row display).
const buildTransferGuideData = (
  t: UnifiedTransfer
): InternalTransferGuideData => {
  const lines: InternalTransferGuideData["lines"] = t.product
    ? [
        {
          codigo: t.product.sku ?? "—",
          item: t.product.name,
          um: getProductionUnitLabel(t.product.unit, t.product.boxes),
          lote: t.product.lote,
          cantidad: t.product.boxes,
        },
      ]
    : (t.ingredientItems ?? []).map((it) => {
        const isOilKg =
          it.ingredient_type === "oil" && !!it.oil_type?.density_kg_per_liter;
        return {
          codigo: it.oil_type?.code ?? it.raw_material?.code ?? "—",
          item: ingredientItemName(it),
          um: isOilKg ? "kg" : it.unit,
          lote: "—",
          cantidad: isOilKg
            ? it.quantity * (it.oil_type?.density_kg_per_liter ?? 1)
            : it.quantity,
        };
      });
  return {
    transferNumber: t.transferNumber,
    transferredAt: t.createdAt,
    fromLabel: t.fromLabel,
    toLabel: t.toLabel,
    performedBy: t.performer,
    notes: t.notes,
    lines,
  };
};

// Both PT sources (bodega product_transfers + un-ingested fg_plant_transfers) read as
// one unified concept: "Producto Terminado".
const TYPE_LABELS: Record<TransferKind, string> = {
  ingredient: "Ingredientes",
  product: "Producto Terminado",
  fg_plant: "Producto Terminado",
};

const statusChip = (t: UnifiedTransfer) => {
  if (t.kind === "product")
    return (
      PRODUCT_STATUS_META[t.status] ?? { label: t.status, className: NEUTRAL_CHIP }
    );
  // FG-between-plants completes instantly; show the same "Recibido" as a received PT
  // so both finished-goods transfers read alike.
  if (t.kind === "fg_plant") return PRODUCT_STATUS_META.completed;
  return { label: "Completado", className: NEUTRAL_CHIP };
};

export function TrasladosPage() {
  const { toast } = useToast();
  const { data: ingredientTransfers = [], isLoading: loadingIngredients } =
    useIngredientTransfers();
  const { data: productTransfers = [], isLoading: loadingProducts } =
    useProductTransfers();
  const { data: fgPlantTransfers = [], isLoading: loadingFgPlant } =
    useFgPlantTransfers();
  const { data: pendingReceipts = [] } = usePendingReceipts();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [tab, setTab] = useState("historial");
  const [printingId, setPrintingId] = useState<string | null>(null);

  // Report filters. "pt" groups both finished-goods sources (product + fg_plant).
  const [typeFilter, setTypeFilter] = useState<"all" | "ingredient" | "pt">(
    "all"
  );
  const [originFilter, setOriginFilter] = useState("all");
  const [destFilter, setDestFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const isLoading = loadingIngredients || loadingProducts || loadingFgPlant;

  const printGuideError = () =>
    toast({
      title: "Error",
      description: "No se pudo generar la guía de traslado.",
      variant: "destructive",
    });

  // History rows: build the guía straight from the loaded row (works for every kind).
  const handlePrintRow = async (t: UnifiedTransfer) => {
    setPrintingId(t.id);
    try {
      await generateInternalTransferPdf(buildTransferGuideData(t));
    } catch {
      printGuideError();
    } finally {
      setPrintingId(null);
    }
  };

  // Recepción pendiente: product transfers by id (fetch the doc data).
  const handlePrintProduct = async (transferId: string) => {
    setPrintingId(transferId);
    try {
      const data =
        await productTransferService.getProductTransferForDocument(transferId);
      await generateInternalTransferPdf(data);
    } catch {
      printGuideError();
    } finally {
      setPrintingId(null);
    }
  };

  const transfers = useMemo<UnifiedTransfer[]>(() => {
    const merged = [
      ...ingredientTransfers.map(normalizeIngredient),
      ...productTransfers.map(normalizeProduct),
      ...fgPlantTransfers.map(normalizeFgPlant),
    ];
    return merged.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [ingredientTransfers, productTransfers, fgPlantTransfers]);

  // Origin/destination options are the distinct route labels present in the data
  // (covers plants P1/P2/NC and bodegas B1/B2 without hardcoding either namespace).
  const originOptions = useMemo(
    () => Array.from(new Set(transfers.map((t) => t.fromLabel))).sort(),
    [transfers]
  );
  const destOptions = useMemo(
    () => Array.from(new Set(transfers.map((t) => t.toLabel))).sort(),
    [transfers]
  );

  const filtered = useMemo(
    () =>
      transfers.filter((t) => {
        if (typeFilter === "ingredient" && t.kind !== "ingredient") return false;
        if (
          typeFilter === "pt" &&
          t.kind !== "product" &&
          t.kind !== "fg_plant"
        )
          return false;
        if (originFilter !== "all" && t.fromLabel !== originFilter) return false;
        if (destFilter !== "all" && t.toLabel !== destFilter) return false;
        if (dateFrom && dayjs(t.createdAt).isBefore(dayjs(dateFrom), "day"))
          return false;
        if (dateTo && dayjs(t.createdAt).isAfter(dayjs(dateTo), "day"))
          return false;
        return true;
      }),
    [transfers, typeFilter, originFilter, destFilter, dateFrom, dateTo]
  );

  const hasActiveFilters =
    typeFilter !== "all" ||
    originFilter !== "all" ||
    destFilter !== "all" ||
    !!dateFrom ||
    !!dateTo;

  const { page, setPage, pageCount, pageItems, total } = usePagination(
    filtered,
    10
  );

  const handleExport = () => {
    const headers = [
      "N°",
      "Fecha",
      "Tipo",
      "Estado",
      "Origen",
      "Destino",
      "Detalle",
      "Realizado por",
      "Notas",
    ];
    const rows = filtered.map((t) => [
      t.transferNumber,
      dayjs(t.createdAt).format("DD/MM/YYYY HH:mm"),
      TYPE_LABELS[t.kind],
      statusChip(t).label,
      t.fromLabel,
      t.toLabel,
      t.summary,
      t.performer,
      t.notes ?? "",
    ]);
    exportCsv(`traslados_${dayjs().format("YYYYMMDD")}.csv`, headers, rows);
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeaderSkeleton withAction />
        <TableSkeleton rows={8} columns={7} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Traslados"
        description="Mueve stock entre plantas (Planta 1 ↔ Planta 2 ↔ No Conforme): ingredientes y producto terminado. El producto terminado ya ingresado viaja en tránsito hasta que el destino confirme la recepción."
        actions={
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            Nuevo Traslado
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 sm:w-auto sm:inline-flex">
          <TabsTrigger value="historial">Historial</TabsTrigger>
          <TabsTrigger value="recepcion" className="gap-2">
            Recepción pendiente
            {pendingReceipts.length > 0 && (
              <Badge
                variant="secondary"
                className="border-amber-300 bg-amber-50 text-amber-700"
              >
                {pendingReceipts.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="historial"
          className="space-y-4 duration-300 animate-in fade-in-0 slide-in-from-bottom-1"
        >
          {transfers.length === 0 ? (
            <EmptyState
              icon={ArrowLeftRight}
              title="Sin traslados"
              description="Aún no se han registrado traslados de ingredientes ni de producto terminado."
              action={
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="mr-1 h-4 w-4" />
                  Nuevo Traslado
                </Button>
              }
            />
          ) : (
            <>
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">Tipo</span>
                  <Select
                    value={typeFilter}
                    onValueChange={(v) =>
                      setTypeFilter(v as "all" | "ingredient" | "pt")
                    }
                  >
                    <SelectTrigger className="h-9 w-full sm:w-[190px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los tipos</SelectItem>
                      <SelectItem value="ingredient">Ingredientes</SelectItem>
                      <SelectItem value="pt">Producto Terminado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">Origen</span>
                  <Select value={originFilter} onValueChange={setOriginFilter}>
                    <SelectTrigger className="h-9 w-full sm:w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {originOptions.map((o) => (
                        <SelectItem key={o} value={o}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">Destino</span>
                  <Select value={destFilter} onValueChange={setDestFilter}>
                    <SelectTrigger className="h-9 w-full sm:w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {destOptions.map((o) => (
                        <SelectItem key={o} value={o}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">Desde</span>
                  <Input
                    type="date"
                    value={dateFrom}
                    max={dateTo || undefined}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="h-9 w-full sm:w-[150px]"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">Hasta</span>
                  <Input
                    type="date"
                    value={dateTo}
                    min={dateFrom || undefined}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="h-9 w-full sm:w-[150px]"
                  />
                </div>
                <div className="flex flex-1 items-end justify-end">
                  <Button
                    variant="outline"
                    className="h-9"
                    onClick={handleExport}
                    disabled={filtered.length === 0}
                  >
                    <Download className="mr-1 h-4 w-4" />
                    Descargar CSV
                  </Button>
                </div>
              </div>

              {filtered.length === 0 ? (
                <EmptyState
                  icon={ArrowLeftRight}
                  title="Sin resultados"
                  description="Ningún traslado coincide con los filtros seleccionados."
                />
              ) : (
                <Card>
                  <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8" />
                      <TableHead>N°</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Traslado</TableHead>
                      <TableHead>Detalle</TableHead>
                      <TableHead>Realizado por</TableHead>
                      <TableHead className="text-right">Guía</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageItems.map((t, index) => {
                      const isExpanded = expandedId === t.id;
                      const chip = statusChip(t);
                      return (
                        <Fragment key={t.id}>
                          <TableRow
                            className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both cursor-pointer hover:bg-muted/30"
                            style={{
                              animationDelay: `${Math.min(index * 40, 400)}ms`,
                            }}
                            onClick={() =>
                              setExpandedId(isExpanded ? null : t.id)
                            }
                          >
                            <TableCell>
                              <ChevronRight
                                className={cn(
                                  "h-4 w-4 text-muted-foreground transition-transform duration-200",
                                  isExpanded && "rotate-90"
                                )}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              {t.transferNumber}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {dayjs(t.createdAt).format("DD/MM/YYYY HH:mm")}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className="whitespace-nowrap"
                              >
                                {TYPE_LABELS[t.kind]}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={cn("whitespace-nowrap", chip.className)}
                              >
                                {chip.label}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                                {t.fromLabel}
                                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                                {t.toLabel}
                              </span>
                            </TableCell>
                            <TableCell className="max-w-[260px] truncate text-muted-foreground">
                              {t.summary}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {t.performer}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-9 w-9 p-0"
                                disabled={printingId === t.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePrintRow(t);
                                }}
                                title="Imprimir guía de traslado"
                              >
                                {printingId === t.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Printer className="h-4 w-4" />
                                )}
                              </Button>
                            </TableCell>
                          </TableRow>

                          {isExpanded && (
                            <TableRow className="animate-in fade-in-0 slide-in-from-top-1 bg-muted/20 duration-200 hover:bg-muted/20">
                              <TableCell />
                              <TableCell colSpan={8}>
                                <div className="space-y-1.5 py-1">
                                  {t.kind === "ingredient" &&
                                    t.ingredientItems?.map((it) => (
                                      <div
                                        key={it.id}
                                        className="flex items-center justify-between gap-4 text-sm"
                                      >
                                        <span className="text-gray-700">
                                          {ingredientItemName(it)}
                                        </span>
                                        <span className="text-muted-foreground">
                                          {it.ingredient_type === "oil" &&
                                          it.oil_type?.density_kg_per_liter
                                            ? `${fmtQty(
                                                it.quantity *
                                                  it.oil_type.density_kg_per_liter
                                              )} kg`
                                            : `${fmtQty(it.quantity)} ${it.unit}`}
                                        </span>
                                      </div>
                                    ))}
                                  {t.product && (
                                    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 text-sm">
                                      <span className="flex items-center gap-2 text-gray-700">
                                        {t.product.sku && (
                                          <span className="font-mono text-xs text-muted-foreground">
                                            {t.product.sku}
                                          </span>
                                        )}
                                        {t.product.name}
                                      </span>
                                      <span className="text-muted-foreground">
                                        Lote: {t.product.lote}
                                      </span>
                                      <span className="text-muted-foreground">
                                        {fmtQty(t.product.boxes)}{" "}
                                        {getProductionUnitLabel(
                                          t.product.unit,
                                          t.product.boxes
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  {t.notes && (
                                    <div className="pt-1 text-sm text-muted-foreground">
                                      Notas: {t.notes}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {pageCount > 1 && (
                <TablePagination
                  page={page}
                  pageCount={pageCount}
                  totalItems={total}
                  pageSize={10}
                  onPageChange={setPage}
                  itemLabel="traslados"
                />
              )}
            </>
          )}
        </TabsContent>

        <TabsContent
          value="recepcion"
          className="duration-300 animate-in fade-in-0 slide-in-from-bottom-1"
        >
          <TransferReceptionTab
            onPrint={handlePrintProduct}
            printingId={printingId}
          />
        </TabsContent>
      </Tabs>

      <NewTransferDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </PageContainer>
  );
}
